import { getBackgroundColorHex } from '../../../bridge/utils/dataUtils/cell/tableCellStyleUtils';
import { getIsColorDarkerThan, mixColorsForOverlayEffect } from '../../../../../../common/colors/coreColorUtil';
import { getIsLightElementColor } from '../../../../../../common/colors/colorComparisonUtil';
import { getHotTableCellData } from '../../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import type { MNHotInstance } from '../../../bridge/tableHotInstanceTypes';
import { isHotInstanceAvailable } from '../../../bridge/utils/hotInstanceUtils/hotTableInstanceValidationUtils';

/**
 * Returns the border overlap state for a neighbouring cell.
 */
const getNeighbourCellColorInfo = (
    row: number,
    col: number,
    currentColor: string,
    instance: MNHotInstance,
): { color: string | null; shouldOverlapCurrentCell: boolean } => {
    if (row >= instance.countRows() || row < 0 || col >= instance.countCols() || col < 0) {
        return { color: null, shouldOverlapCurrentCell: false };
    }

    const cellData = getHotTableCellData(instance, { row, col });
    if (!cellData?.background) return { color: null, shouldOverlapCurrentCell: false };

    const isDarkMode = instance.milanoteProps.isDarkMode || false;
    const hexColor = getBackgroundColorHex(cellData.background, isDarkMode);
    const isDarker = !!cellData.background && getIsColorDarkerThan(hexColor, currentColor);

    return {
        color: hexColor,
        shouldOverlapCurrentCell: (isDarkMode ? !isDarker : isDarker) && !!hexColor,
    };
};

export const colorBorders = (hot: MNHotInstance, row: number, col: number) => {
    const cellData = getHotTableCellData(hot, { row, col });
    if (!cellData?.background || !hot) return;

    const hexColor = getBackgroundColorHex(cellData.background, hot.milanoteProps.isDarkMode || false);
    const defaultBorderColor = getIsLightElementColor(hexColor) ? 'rgb(50, 59, 74)' : 'rgb(255, 255, 255)';
    const borderColor = mixColorsForOverlayEffect(hexColor, defaultBorderColor).toString();

    const cellAbove = getNeighbourCellColorInfo(row - 1, col, hexColor, hot);
    const shouldColorTopBorder = !cellAbove.shouldOverlapCurrentCell;

    const cellLeft = getNeighbourCellColorInfo(row, col - 1, hexColor, hot);
    const shouldColorLeftBorder = !cellLeft.shouldOverlapCurrentCell && col !== 0;

    requestAnimationFrame(() => {
        if (!isHotInstanceAvailable(hot)) return;

        const td = hot.getCell(row, col);
        if (!td) return;

        const renderer = td.querySelector('.MilanoteCellRendererComponent') as HTMLElement;
        if (!renderer) return;

        renderer.classList.remove(
            'colored-border-top-only',
            'colored-border-left-only',
            'colored-no-border-top-right-corner',
            'colored-border-bottom-right-corner',
        );

        td.style.borderColor = 'transparent';
        if (col !== hot.countCols() - 1) td.style.borderRightColor = borderColor;
        td.style.borderBottomColor = borderColor;

        renderer.style.borderColor = 'transparent';
        if (shouldColorTopBorder && shouldColorLeftBorder) {
            renderer.style.borderColor = borderColor;
        } else if (shouldColorTopBorder) {
            renderer.style.borderTopColor = borderColor;
            renderer.classList.add('colored-border-top-only');
        } else if (shouldColorLeftBorder) {
            renderer.style.borderLeftColor = borderColor;
            renderer.classList.add('colored-border-left-only');
        }

        const cellTopRight = getNeighbourCellColorInfo(row - 1, col + 1, hexColor, hot);
        if (cellTopRight.shouldOverlapCurrentCell) {
            renderer.classList.add('colored-no-border-top-right-corner');
        }

        const cellBottomRight = getNeighbourCellColorInfo(row + 1, col + 1, hexColor, hot);
        if (!!cellBottomRight.color && !cellBottomRight.shouldOverlapCurrentCell) {
            renderer.classList.add('colored-border-bottom-right-corner');
        }
    });
};
